import { useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import type { TranslationKeys } from '../../i18n/types.js';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { normalize, rankItems } from '../../lib/search/engine';
import { contentSnippet } from '../../lib/search/content';

/**
 * The app-wide search (Ctrl+K) — one look at the table and in Подготовка. The host builds the entries (what the
 * person may see — see searchSources.ts); this owns the query, the ranking (lib/search/engine), the groups, the
 * keyboard and the overlay.
 *
 * Keys: ↑/↓ — the row, Enter — open (or the chosen action), →/← — the row's actions («На карту», «Показать
 * игрокам…»…), Esc — close. An action that changes what players see only ASKS (the host's existing confirmation).
 */

export type SearchKind =
  | 'action'
  | 'beat'
  | 'journal'
  | 'page'
  | 'sheet'
  | 'token'
  | 'timeline'
  | 'monster'
  | 'spell'
  | 'condition'
  | 'item';

export interface SearchAction {
  readonly id: string;
  readonly label: string;
  readonly run: () => void;
  /** Shown but not runnable now (e.g. «На карту» with no map open); the title says why. */
  readonly disabledReason?: string;
}

export interface SearchEntry {
  readonly id: string;
  readonly kind: SearchKind;
  readonly label: string;
  /** More names it is found by: the English name of a Russian label, a monster's alias. */
  readonly names?: readonly string[];
  readonly content?: string;
  readonly snippet?: string;
  /** A short caption after the label: «в бите «Засада»», «CR 1/4», «3 ур.». */
  readonly hint?: string;
  readonly run: () => void;
  readonly actions?: readonly SearchAction[];
  /** 0…1 — the current context (the running beat, the open map): first among equals. */
  readonly boost?: number;
  /** Listed only for a typed query (the SRD — a thousand rows would bury the campaign). */
  readonly searchOnly?: boolean;
}

export interface SearchPaletteProps {
  readonly open: boolean;
  readonly entries: readonly SearchEntry[];
  readonly t: (key: keyof TranslationKeys) => string;
  readonly onClose: () => void;
  readonly placeholder: string;
  /** What is searched, said under «Nothing found». */
  readonly scopeHint: string;
  /** The SRD names are still on their way. */
  readonly loading?: boolean;
  /** `studio-palette` in Подготовка (the e2e of the packages address it), `table-search` at the table. */
  readonly testId: string;
  /**
   * A picker of the same look (the gallery's «Использовать как…»): its own group names, its own name of the empty
   * query's top group, its picks kept out of the search's «Недавнее» (the same `sheet:id` keys), its own layer.
   */
  readonly groupLabels?: Partial<Record<SearchKind, keyof TranslationKeys>>;
  readonly topLabel?: keyof TranslationKeys;
  readonly rememberPicks?: boolean;
  readonly zIndex?: number;
}

const GROUP_ORDER: readonly SearchKind[] = ['action', 'beat', 'sheet', 'token', 'journal', 'page', 'timeline', 'monster', 'spell', 'condition', 'item'];
const GROUP_KEY: Readonly<Record<SearchKind, keyof TranslationKeys>> = {
  action: 'search.group.action',
  beat: 'search.group.beat',
  journal: 'search.group.journal',
  page: 'search.group.page',
  sheet: 'search.group.sheet',
  token: 'search.group.token',
  timeline: 'search.group.timeline',
  monster: 'search.group.monster',
  spell: 'search.group.spell',
  condition: 'search.group.condition',
  item: 'search.group.item',
};
/** Rows per group for a typed query; the actions of the app are few and short. */
const PER_GROUP = 8;
/** Rows of the empty query: what was opened lately and the context, then the actions and the campaign. */
const EMPTY_MAX = 40;
const RECENT_MAX = 20;

const keyOf = (e: Pick<SearchEntry, 'kind' | 'id'>): string => `${e.kind}:${e.id}`;

/** What was picked lately, newest first — lives for the page load, across the table ⇄ Подготовка swap. */
const recent: string[] = [];
function remember(entry: SearchEntry): void {
  if (entry.kind === 'action') return;
  const key = keyOf(entry);
  const at = recent.indexOf(key);
  if (at !== -1) recent.splice(at, 1);
  recent.unshift(key);
  recent.length = Math.min(recent.length, RECENT_MAX);
}
function recentBoost(entry: SearchEntry): number {
  const at = recent.indexOf(keyOf(entry));
  return at === -1 ? 0 : 0.6 * (1 - at / RECENT_MAX);
}

interface Group {
  readonly kind: SearchKind | 'recent' | 'text';
  readonly rows: readonly SearchEntry[];
}

function groupsFor(query: string, entries: readonly SearchEntry[]): Group[] {
  if (normalize(query) === '') {
    const listed = entries.filter((e) => e.searchOnly !== true);
    const top = listed
      .filter((e) => e.kind !== 'action' && ((e.boost ?? 0) > 0 || recentBoost(e) > 0))
      .sort((a, b) => Math.max(b.boost ?? 0, recentBoost(b)) - Math.max(a.boost ?? 0, recentBoost(a)))
      .slice(0, 6);
    const taken = new Set(top.map(keyOf));
    const out: Group[] = top.length > 0 ? [{ kind: 'recent', rows: top }] : [];
    let budget = EMPTY_MAX - top.length;
    for (const kind of GROUP_ORDER) {
      if (budget <= 0) break;
      const rows = listed.filter((e) => e.kind === kind && !taken.has(keyOf(e))).slice(0, Math.min(budget, kind === 'action' ? 12 : PER_GROUP));
      if (rows.length === 0) continue;
      out.push({ kind, rows });
      budget -= rows.length;
    }
    return out;
  }
  const ranked = rankItems(query, entries, (e) => [e.label, ...(e.names ?? [])], {
    boost: (e) => Math.max(e.boost ?? 0, recentBoost(e)),
  });
  const byKind = new Map<SearchKind, { best: number; rows: SearchEntry[] }>();
  for (const r of ranked) {
    const g = byKind.get(r.item.kind);
    if (g === undefined) byKind.set(r.item.kind, { best: r.score, rows: [r.item] });
    else if (g.rows.length < PER_GROUP) g.rows.push(r.item);
  }
  const groups: Group[] = [...byKind.entries()]
    .sort((a, b) => b[1].best - a[1].best || GROUP_ORDER.indexOf(a[0]) - GROUP_ORDER.indexOf(b[0]))
    .map(([kind, g]) => ({ kind, rows: g.rows }));
  const named = new Set(ranked.map(r => keyOf(r.item)));
  const textRows: SearchEntry[] = [];
  for (const entry of entries) {
    if (!entry.content || named.has(keyOf(entry))) continue;
    const snippet = contentSnippet(query, entry.content);
    if (snippet) textRows.push({ ...entry, snippet });
  }
  if (textRows.length) groups.push({ kind: 'text', rows: textRows.slice(0, 12) });
  return groups;
}

export function SearchPalette({ open, entries, t, onClose, placeholder, scopeHint, loading = false, testId, groupLabels, topLabel, rememberPicks = true, zIndex }: SearchPaletteProps): JSX.Element | null {
  const [query, setQuery] = useState<string>('');
  const [activeIndex, setActiveIndex] = useState<number>(0);
  // 0 — the row itself (open); 1… — its actions.
  const [actionIndex, setActionIndex] = useState<number>(0);
  const listRef = useRef<HTMLUListElement>(null);

  // Every opening starts from a clean query — cleared when the palette CLOSES. Clearing on open (an effect after the
  // first render) wiped what was typed right after Ctrl+K: Enter then opened the first row of the empty list (the
  // current beat) instead of the typed command (package31 «5», seen in its trace).
  useEffect((): void => {
    if (!open) {
      setQuery('');
      setActiveIndex(0);
      setActionIndex(0);
    }
  }, [open]);

  const groups = useMemo((): Group[] => (open ? groupsFor(query, entries) : []), [open, query, entries]);
  const flat = useMemo((): SearchEntry[] => groups.flatMap((g) => g.rows), [groups]);

  useEffect((): void => {
    if (activeIndex >= flat.length) setActiveIndex(0);
  }, [flat.length, activeIndex]);
  useEffect((): void => setActionIndex(0), [activeIndex, query]);
  useEffect((): void => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!open) return null;

  // The chosen row never points past the list (a stale index after the list shrank picks the first, not nothing).
  const current: number = activeIndex < flat.length ? activeIndex : 0;
  const active: SearchEntry | undefined = flat[current];
  const runnable = (entry: SearchEntry): SearchAction[] => (entry.actions ?? []).filter((a) => a.disabledReason === undefined);
  const pick = (entry: SearchEntry, action?: SearchAction): void => {
    if (action?.disabledReason !== undefined) return;
    if (rememberPicks) remember(entry);
    onClose();
    (action?.run ?? entry.run)();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex(flat.length === 0 ? 0 : (current + step + flat.length) % flat.length);
      return;
    }
    // →/← walk the actions of the row only when the caret is at the end of the query (else they move the caret).
    if ((event.key === 'ArrowRight' || event.key === 'ArrowLeft') && active !== undefined && runnable(active).length > 0) {
      const input = event.currentTarget;
      const atEnd = input.selectionStart === input.value.length && input.selectionEnd === input.value.length;
      if (event.key === 'ArrowRight' && !atEnd && actionIndex === 0) return;
      if (event.key === 'ArrowLeft' && actionIndex === 0) return;
      event.preventDefault();
      const count = runnable(active).length + 1;
      setActionIndex((prev) => (prev + (event.key === 'ArrowRight' ? 1 : -1) + count) % count);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (active === undefined) return;
      pick(active, actionIndex === 0 ? undefined : runnable(active)[actionIndex - 1]);
    }
  };

  let rowIndex = -1;
  return (
    <div
      className="search-palette fixed inset-0 z-50 flex items-start justify-center bg-shade/50 pt-24"
      data-testid={testId}
      role="dialog"
      aria-modal="true"
      aria-label={placeholder}
      style={zIndex === undefined ? undefined : { zIndex }}
      onMouseDown={(event): void => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[calc(100vh-10rem)] w-full max-w-xl flex-col overflow-hidden rounded-xl border border-line bg-panel shadow-2xl">
        <Input
          autoFocus
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-controls={`${testId}-list`}
          aria-activedescendant={active === undefined ? undefined : `${testId}-row-${current}`}
          className="h-auto rounded-none border-0 border-b border-line bg-transparent px-4 py-3 text-ui focus-visible:ring-0 pointer-coarse:h-auto"
          data-testid={`${testId}-input`}
          placeholder={placeholder}
          aria-label={placeholder}
          value={query}
          onChange={(event): void => {
            // A new query starts from its first row at once — not after an effect (Enter may come before it).
            setQuery(event.target.value);
            setActiveIndex(0);
            setActionIndex(0);
          }}
          onKeyDown={handleKeyDown}
        />
        <ul ref={listRef} id={`${testId}-list`} role="listbox" className="search-palette__list min-h-0 flex-1 overflow-y-auto py-1.5">
          {flat.length === 0 ? (
            <li className="flex flex-col gap-1 px-4 py-3" data-testid="search-empty">
              <span className="text-ui text-fg">
                {query.trim() === '' ? t('studio.palette.empty') : t('search.empty').replace('{q}', query.trim())}
              </span>
              {loading ? null : <span className="text-meta text-dim">{scopeHint}</span>}
            </li>
          ) : (
            groups.map((group) => (
              <li key={group.kind} role="presentation" className="pb-1">
                <div className="px-4 pb-0.5 pt-1.5 text-label font-semibold uppercase tracking-label text-dim" data-testid="search-group">
                  {t(group.kind === 'recent' ? (topLabel ?? 'search.group.recent') : group.kind === 'text' ? 'search.group.text' : (groupLabels?.[group.kind] ?? GROUP_KEY[group.kind]))}
                </div>
                <ul role="group" aria-label={t(group.kind === 'recent' ? (topLabel ?? 'search.group.recent') : group.kind === 'text' ? 'search.group.text' : (groupLabels?.[group.kind] ?? GROUP_KEY[group.kind]))}>
                  {group.rows.map((entry) => {
                    rowIndex += 1;
                    const index = rowIndex;
                    const isActive = index === current;
                    const acts = entry.actions ?? [];
                    let runIdx = 0;
                    return (
                      <li
                        key={keyOf(entry)}
                        id={`${testId}-row-${index}`}
                        role="option"
                        aria-selected={isActive}
                        data-active={isActive ? 'true' : undefined}
                        data-kind={entry.kind}
                        className={`search-palette__item studio-palette__item flex cursor-pointer items-center gap-2 px-4 py-1.5 text-ui ${
                          isActive && actionIndex === 0 ? 'bg-hl/10 text-fg' : isActive ? 'bg-panel-2 text-fg' : 'text-fg hover:bg-panel-2'
                        }`}
                        // Only a pointer that MOVES chooses a row: the list opening under a resting pointer (Chromium sends a
                        // mouseenter then) must not take the choice away from the first row.
                        onMouseMove={(): void => {
                          if (index !== current) setActiveIndex(index);
                        }}
                        onClick={(): void => pick(entry)}
                      >
                        <span className="min-w-0 flex-1 truncate">
                          {entry.label}
                          {entry.hint === undefined || entry.hint === '' ? null : <span className="ml-2 text-meta text-dim">{entry.hint}</span>}
                          {entry.snippet && <span className="mt-1 block whitespace-normal break-words text-meta text-muted-foreground" data-testid="search-snippet">{entry.snippet}</span>}
                        </span>
                        {acts.length > 0 ? (
                          <span className={`flex flex-none items-center gap-2 ${isActive ? '' : 'invisible'}`}>
                            {acts.map((act) => {
                              const usable = act.disabledReason === undefined;
                              if (usable) runIdx += 1;
                              const chosen = isActive && usable && actionIndex === runIdx;
                              return (
                                <Button
                                  key={act.id}
                                  type="button"
                                  variant="text"
                                  size="inline"
                                  tabIndex={-1}
                                  data-testid={`search-act-${act.id}`}
                                  data-chosen={chosen ? 'true' : undefined}
                                  disabled={!usable}
                                  title={act.disabledReason ?? act.label}
                                  className={`text-meta disabled:pointer-events-auto disabled:cursor-not-allowed disabled:text-dim disabled:no-underline ${chosen ? 'underline' : ''}`}
                                  onClick={(event): void => {
                                    event.stopPropagation();
                                    pick(entry, act);
                                  }}
                                >
                                  {act.label}
                                </Button>
                              );
                            })}
                          </span>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))
          )}
          {loading && query.trim() !== '' ? (
            <li className="px-4 py-1.5 text-meta text-dim" data-testid="search-loading">{t('search.loadingSrd')}</li>
          ) : null}
        </ul>
        <div className="border-t border-line px-4 py-1.5 text-label text-dim">{t('search.keys')}</div>
      </div>
    </div>
  );
}
